import { ListingStatusEnum } from "../common/commonEnums";
import { ParsedUrlQuery } from "querystring";
import {
  IMarketFilters,
  IQueryParams,
  MarketCategoryFilterType,
  MarketSortByType,
} from "../common/commonTypes";
import { getPriorDate } from "./dateTime";
import { getQueryParam } from "./nextHelper";

interface IFilterItem {
  [attribute: string]: {
    [selector: string]: string;
  };
}

export const getMarketFilterValues = (
  query: ParsedUrlQuery
): IMarketFilters => {
  const category = getQueryParam(query, "category");

  return {
    category: category && isCategoryFilterType(category) ? category : "all",
    lang: getQueryParam(query, "lang"),
    genre: getQueryParam(query, "genre"),
    sortBy: getQueryParam(query, "sortBy") as MarketSortByType,
    sale: getQueryParam(query, "sale"),
  };
};

export const getMarketQueryFilters = ({
  category,
  lang,
  genre,
  sortBy,
  sale,
}: IMarketFilters) => {
  const categoryParam = getFilterCategoryFilterParam(category);
  const selector = "$contains";
  const langParam = getLangFilterParam(lang, selector);
  const genreParam = getGenreFilterParam(genre, selector);
  const sortByParam = getSortByParam(sortBy);
  const saleParam = sale === "1";

  let param: IQueryParams = {
    where: {
      $and: [
        {
          status: saleParam
            ? { $eq: ListingStatusEnum.active }
            : {
                $notIn: [
                  ListingStatusEnum.cancelled,
                  ListingStatusEnum.ended,
                  ListingStatusEnum.failed,
                ],
              },
        },
        { "$artist.approved_artist$": { $eq: true } },
      ],
    },
    order: sortByParam ? sortByParam.order : [["created", "DESC"]],
  };

  if (categoryParam) {
    param.where.$and.push(categoryParam);
  }

  if (category === "hot") {
    param.order = [["views", "DESC"], ...param.order];
  }

  if (sortBy === "endingSoon") {
    param.where.$and.push({ status: ListingStatusEnum.active });
  }

  const condition = "$and";

  if (!isEmpty(langParam)) {
    param.where[condition].push(langParam);
  }
  if (!isEmpty(genreParam)) {
    param.where[condition].push(genreParam);
  }

  if (category === "all" && !sortBy) {
    // default sorting: sold, latest end, created
    param.order = [
      ["status", "DESC"],
      ["ending", "DESC"],
      ["created", "DESC"],
      ...param.order,
    ];
  }

  return param;
};

function isEmpty(filter: IFilterItem): boolean {
  const selector = Object.values(filter)[0];
  const value = Object.values(selector)[0];

  return value === "{}";
}

export const getFilterCategoryFilterParam = (
  filter?: MarketCategoryFilterType
) => {
  switch (filter) {
    case "drops":
      return {
        created: { $gte: getPriorDate(90) },
      };

    case "hot":
      return {
        created: { $gte: getPriorDate(90) },
      };

    default:
      return {};
  }
};

const getLangFilterParam = (
  value?: string,
  selector: "$overlap" | "$contains" = "$contains"
): IFilterItem => {
  return {
    "$music.languages$": {
      [selector]: queryParamArrayToFilterValue(value),
    },
  };
};

const getGenreFilterParam = (
  value?: string,
  selector: "$overlap" | "$contains" = "$contains"
): IFilterItem => {
  return {
    "$music.genres$": {
      [selector]: queryParamArrayToFilterValue(value),
    },
  };
};

// this is a hack because the API expects:
// { $contains: {AF, ZA} }
// and not { $contains: ["AF", "ZA"] }
function queryParamArrayToFilterValue(value?: string) {
  const values = value?.split(",") || [""];

  return `{${values.join(", ").replace(/, $/gm, "")}}`;
}

export const getSortByParam = (sortBy?: MarketSortByType) => {
  switch (sortBy) {
    case "highestPrice":
      return {
        order: [["amount", "DESC"]],
      };

    case "lowestPrice":
      return {
        order: [["amount", "ASC"]],
      };

    case "endingSoon":
      return {
        order: [["ending", "ASC"]],
      };

    case "oldest":
      return {
        order: [["created", "ASC"]],
      };
    default:
      return {
        order: [["created", "DESC"]],
      };
  }
};

export const isCategoryFilterType = (
  value: string
): value is MarketCategoryFilterType => {
  return ["all", "drops", "hot"].includes(value);
};

export const isMarketSortByType = (
  value: string
): value is MarketSortByType => {
  return ["highestPrice", "lowestPrice", "endingSoon", "oldest"].includes(
    value
  );
};
